/**
 * Create-space modal — POSTs to /api/v1/spaces, navigates to the new space on success.
 *
 * Slug is auto-derived from the name on the backend; user can override.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type CSSProperties } from "react";
import { useNavigate } from "react-router-dom";

import { api } from "@/lib/api";
import type { Space } from "@/lib/types";

type Props = {
  open: boolean;
  onClose: () => void;
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63);
}

export function CreateSpaceModal({ open, onClose }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [icon, setIcon] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setName("");
    setSlug("");
    setSlugTouched(false);
    setIcon("");
    setDescription("");
  }, [open]);

  useEffect(() => {
    if (!slugTouched) setSlug(slugify(name));
  }, [name, slugTouched]);

  const submit = useMutation({
    mutationFn: async () => {
      const body = {
        name: name.trim(),
        slug: slug.trim() || null,
        icon: icon.trim() || null,
        description: description.trim() || null,
      };
      return (await api.post<Space>("/spaces", body)).data;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["spaces"] });
      onClose();
      navigate(`/wiki/spaces/${created.slug}`);
    },
    onError: (err) => {
      const detail =
        (err as { response?: { data?: { detail?: unknown } } }).response?.data
          ?.detail;
      setError(
        typeof detail === "string"
          ? detail
          : Array.isArray(detail)
            ? (detail[0] as { msg?: string })?.msg ?? "Could not create space."
            : "Could not create space.",
      );
    },
  });

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal
      aria-labelledby="create-space-title"
      onClick={onClose}
      style={overlay}
    >
      <div onClick={(e) => e.stopPropagation()} style={card}>
        <h2 id="create-space-title" style={{ fontSize: 20, fontWeight: 600, margin: "0 0 16px" }}>
          New space
        </h2>

        <label style={fieldRow}>
          <span style={fieldLabel}>Name</span>
          <input
            autoFocus
            type="text"
            placeholder="Engineering, Product, Design…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={input}
            maxLength={255}
          />
        </label>

        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
          <label>
            <span style={fieldLabel}>Slug</span>
            <input
              type="text"
              placeholder="auto-from-name"
              value={slug}
              onChange={(e) => {
                setSlug(e.target.value);
                setSlugTouched(true);
              }}
              style={{ ...input, fontFamily: "var(--font-mono)" }}
              maxLength={63}
            />
          </label>
          <label>
            <span style={fieldLabel}>Icon (1 char)</span>
            <input
              type="text"
              placeholder="E"
              value={icon}
              onChange={(e) => setIcon(e.target.value.slice(0, 2))}
              style={input}
              maxLength={2}
            />
          </label>
        </div>

        <label style={fieldRow}>
          <span style={fieldLabel}>Description</span>
          <textarea
            placeholder="Optional: what lives in this space?"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{ ...input, resize: "vertical", fontFamily: "var(--font-sans)" }}
            maxLength={1024}
          />
        </label>

        {error && (
          <p
            role="alert"
            style={{
              padding: "8px 12px",
              fontSize: 13,
              color: "#ee1d36",
              background: "rgba(238,29,54,0.06)",
              border: "1px solid rgba(238,29,54,0.3)",
              borderRadius: 4,
              marginTop: 12,
            }}
          >
            {error}
          </p>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
          <button type="button" onClick={onClose} style={btnSecondary}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setError(null);
              if (!name.trim()) {
                setError("Name is required.");
                return;
              }
              submit.mutate();
            }}
            disabled={submit.isPending}
            style={{
              ...btnPrimary,
              cursor: submit.isPending ? "wait" : "pointer",
              opacity: submit.isPending ? 0.6 : 1,
            }}
          >
            {submit.isPending ? "Creating…" : "Create space"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  backdropFilter: "blur(4px)",
  display: "grid",
  placeItems: "center",
  zIndex: 100,
};

const card: CSSProperties = {
  width: "min(480px, calc(100vw - 32px))",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 8,
  padding: 24,
  boxShadow:
    "0 30px 18px rgba(0,0,0,0.06), 0 13px 13px rgba(0,0,0,0.10), 0 3px 7px rgba(0,0,0,0.12)",
};

const fieldRow: CSSProperties = { display: "block", marginBottom: 12 };

const fieldLabel: CSSProperties = {
  display: "block",
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  color: "var(--mute)",
  marginBottom: 4,
};

const input: CSSProperties = {
  width: "100%",
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 10px",
  fontFamily: "var(--font-sans)",
  fontSize: 14,
  color: "var(--ink)",
  outline: "none",
};

const btnPrimary: CSSProperties = {
  background: "var(--ink)",
  color: "var(--on-primary)",
  border: "none",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  fontWeight: 500,
};

const btnSecondary: CSSProperties = {
  background: "var(--canvas)",
  border: "1px solid var(--hairline)",
  borderRadius: 4,
  padding: "8px 14px",
  fontSize: 13,
  color: "var(--ink)",
  cursor: "pointer",
};
